// Role catalogue for the Add User flow.
//
// The role LIST + codes + display names are owned by Identity (POST /roles/search) — this
// file does not invent them. What it holds is the two pieces Identity's role rows don't
// carry but the provisioning flow needs:
//   1. `id`  — the numeric role id the Admin-Api `UserProvisioning/provision` call expects
//              in its `access` array (Identity itself keys roles by GUID/code, not this int).
//   2. `description` — the permission blurb shown in the "Learn more" tooltip, matching the
//              copy the People Management app already shows operators.
// Both are Admin/product contracts, keyed by the canonical Identity role code.

// Numeric role ids as understood by the Admin-Api provisioning endpoint (the `access`
// array). Mirrors the People Management app's role catalogue so the ids line up
// server-side. Only assignable roles live here: HeadTeacher (10) / GroupDirector (11) are
// NOT picked — they are computed as the provisioning `userRole` from the Owner role plus
// the school/group assignment (see buildProvisioningData). SuperAdmin is never assigned here.
const ROLE_ID_BY_CODE = {
  Analyse: 0,
  Respond: 1,
  Survey_Administration: 2,
  Data_Administration: 3,
  Users_Administration: 4,
  Owner: 5,
  StudentSafeSpaceResponder: 6,
  StudentSafeSpaceManager: 7,
  SafeSpaceAdministrator: 8,
};

// Permission descriptions surfaced in the "Learn more" tooltip (same copy the operator
// already sees in the People Management access-management dialog).
const ROLE_DESCRIPTION_BY_CODE = {
  Analyse: 'Can see the results of surveys and analyse data within Edu Intelligence.',
  Respond: 'Can respond to comments.',
  Survey_Administration: 'Can create, edit and end surveys.',
  Data_Administration:
    'Can manage staff, student and parent data, including Wonde connections.',
  Users_Administration: 'Can add users using the same process you are currently using.',
  Owner: 'Complete access to all functions.',
  StudentSafeSpaceResponder: 'Can respond to Student Safe Space messages.',
  StudentSafeSpaceManager: 'Can manage and escalate Student Safe Space cases.',
  SafeSpaceAdministrator: 'Can administer Student Safe Space settings.',
};

// The roles an operator may assign here. Head Teacher / Group Director / Super Admin are
// provisioned through other flows (their own registration), so they are never offered.
export const ASSIGNABLE_ROLE_CODES = [
  'Analyse',
  'Respond',
  'Survey_Administration',
  'Data_Administration',
  'Users_Administration',
  'Owner',
];

// SafeSpace roles are only meaningful for a single school, so they are offered on top of
// the core set only in a single-school context.
export const SAFE_SPACE_ROLE_CODES = [
  'StudentSafeSpaceResponder',
  'StudentSafeSpaceManager',
  'SafeSpaceAdministrator',
];

// Local labels by role code, used when Identity hasn't supplied a curated `displayName`
// (an older Identity, or the live role fetch is unavailable). They match Identity's seeded
// display names so the picker and the users table read the same either way.
const ROLE_LABEL_BY_CODE = {
  Analyse: 'Analyse',
  Respond: 'Respond',
  Survey_Administration: 'Survey Administration',
  Data_Administration: 'Data Administration',
  Users_Administration: 'User Management',
  Owner: 'Account Owner',
  StudentSafeSpaceResponder: 'Student Safe Space Responder',
  StudentSafeSpaceManager: 'Student Safe Space Manager',
  SafeSpaceAdministrator: 'Safe Space Administrator',
  EI_Chat: 'Edu Intelligence Chat',
  EI_Dashboard: 'Edu Intelligence Dashboard',
  EI_Report_Generation: 'Edu Intelligence Report Generation',
  Context_Management: 'Context Management',
};

// The label shown for a role: Identity's curated `displayName`, else the local label for the
// code, else Identity's raw `name`, else the code itself. Codes never change; only labels do.
export function roleLabel(code, { displayName, name } = {}) {
  return displayName || ROLE_LABEL_BY_CODE[code] || name || code;
}

// Identity's own role names by code. The users table shows labels, but older Identity search
// payloads send names only, and the access-level summary maps those back to codes.
export const FALLBACK_ROLE_NAMES = {
  Analyse: 'Analyse',
  Respond: 'Respond',
  Survey_Administration: 'Survey Administration',
  Data_Administration: 'Data Administration',
  Users_Administration: 'Users Administration',
  Owner: 'Owner',
  StudentSafeSpaceResponder: 'Student Safe Space Responder',
  StudentSafeSpaceManager: 'Student Safe Space Manager',
  SafeSpaceAdministrator: 'Safe Space Administrator',
};

export function roleNumericId(code) {
  return ROLE_ID_BY_CODE[code];
}

export function roleDescription(code) {
  return ROLE_DESCRIPTION_BY_CODE[code] || '';
}

// Turns the raw Identity role rows into the ordered option list the picker renders,
// keeping only the codes assignable in this context and enriching each with its numeric id
// (for provisioning) and description (for the tooltip). Identity owns the code + label; if
// the live fetch is unavailable, `roleLabel` supplies the local label so the picker still
// works. SafeSpace roles are included only when `singleSchool` is true.
export function buildRoleOptions(identityRoles, { singleSchool = false } = {}) {
  const allowed = singleSchool
    ? [...ASSIGNABLE_ROLE_CODES, ...SAFE_SPACE_ROLE_CODES]
    : ASSIGNABLE_ROLE_CODES;

  const identityRoleByCode = {};
  (identityRoles || []).forEach((role) => {
    const code = role?.code ?? role?.Code;
    if (code) {
      identityRoleByCode[code] = {
        displayName: role.displayName ?? role.DisplayName,
        name: role.name ?? role.Name,
      };
    }
  });

  return allowed
    .filter((code) => roleNumericId(code) !== undefined)
    .map((code) => ({
      code,
      id: roleNumericId(code),
      name: roleLabel(code, identityRoleByCode[code]),
      description: roleDescription(code),
    }));
}
